import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

type ToastKind = 'error' | 'success' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextType {
  showToast: (kind: ToastKind, message: string) => void;
  error: (message: string) => void;
  success: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

// Errors stay up longer than confirmations so they aren't missed.
const DURATION_MS: Record<ToastKind, number> = { error: 8000, success: 4000, info: 5000 };

const STYLES: Record<ToastKind, { box: string; Icon: typeof AlertCircle }> = {
  error: {
    box: 'bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
    Icon: AlertCircle,
  },
  success: {
    box: 'bg-green-50 dark:bg-green-900/40 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
    Icon: CheckCircle2,
  },
  info: {
    box: 'bg-blue-50 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
    Icon: Info,
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      window.setTimeout(() => dismiss(id), DURATION_MS[kind]);
    },
    [dismiss]
  );

  const value = useMemo<ToastContextType>(
    () => ({
      showToast,
      error: (m) => showToast('error', m),
      success: (m) => showToast('success', m),
      info: (m) => showToast('info', m),
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Fixed viewport overlay so a toast is visible regardless of scroll position */}
      <div
        aria-live="polite"
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none"
      >
        {toasts.map((t) => {
          const { box, Icon } = STYLES[t.kind];
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex items-start gap-3 p-4 rounded-lg border shadow-lg ${box}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm flex-1 break-words">{t.message}</span>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="flex-shrink-0 opacity-70 hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

/**
 * Pull a human-readable message out of an axios error, falling back to
 * `fallback`. FastAPI puts its message in `response.data.detail`.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return `${fallback}: ${detail}`;
  }
  if (e?.response?.status) {
    return `${fallback} (HTTP ${e.response.status})`;
  }
  if (!e?.response && e?.message) {
    return `${fallback}: ${e.message}`;
  }
  return fallback;
}
